import { useState } from 'react';
import { vendorAssetUrl, type PayToAccount } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';

import { QrDialog } from '@/features/china-payment/components/QrDialog.js';
import { useCopyToClipboard } from '@/lib/clipboard.js';
import { legacyAssetOrigin } from '@/lib/legacy-api.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';

export interface PayToCardProps {
  status: 'loading' | 'ready' | 'error';
  accounts: readonly PayToAccount[];
  onRetry: () => void;
}

/**
 * "Pay to (choose one)" (MYP-61): the MyPoz accounts a China Payment customer transfers to, from
 * `getBankList` — banks with a number to copy, e-wallets with a QR to show.
 *
 * A list beside the request, for reference: the request's step 3 asks which one was paid into
 * (MYP-109), sent as `bankid`. The same four accounts show for every type, as on the old website;
 * whether proxy pay and bank transfer really use them is a question for the vendor (China Payment
 * API issues, item 8).
 */
export function PayToCard({ status, accounts, onRetry }: PayToCardProps) {
  const { t } = useTranslation();
  const [qrFor, setQrFor] = useState<PayToAccount | undefined>(undefined);

  return (
    <Card className="p-0">
      <div className="border-b border-line px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-ink">{t('chinaPayment.payTo.title')}</h2>
        <p className="mt-0.5 text-sm text-muted">{t('chinaPayment.payTo.intro')}</p>
      </div>

      {status === 'loading' ? (
        <p aria-busy="true" className="px-5 py-8 text-center text-sm text-muted sm:px-6">
          {t('chinaPayment.payTo.loading')}
        </p>
      ) : status === 'error' ? (
        <div className="grid justify-items-center gap-3 px-5 py-8 sm:px-6">
          <p className="text-center text-sm text-ink">{t('chinaPayment.payTo.failed')}</p>
          <button type="button" onClick={onRetry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('actions.retry')}
          </button>
        </div>
      ) : accounts.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted sm:px-6">
          {t('chinaPayment.payTo.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {accounts.map((account) => (
            <AccountRow key={account.id} account={account} onShowQr={() => setQrFor(account)} />
          ))}
        </ul>
      )}

      {qrFor?.qrPath ? (
        <QrDialog
          name={qrFor.name}
          {...(qrFor.accountName ? { accountName: qrFor.accountName } : {})}
          qrPath={qrFor.qrPath}
          onClose={() => setQrFor(undefined)}
        />
      ) : null}
    </Card>
  );
}

function AccountRow({ account, onShowQr }: { account: PayToAccount; onShowQr: () => void }) {
  const { t } = useTranslation();
  const { copied, failed, copy } = useCopyToClipboard();
  const { name, accountNumber, accountName, qrPath, logoPath } = account;

  const copyLabel = failed
    ? t('chinaPayment.payTo.copyFailed')
    : copied
      ? t('chinaPayment.payTo.copied')
      : t('chinaPayment.payTo.copy');

  return (
    // Wraps rather than overflowing: on a narrow phone the action drops under the name.
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 sm:flex-nowrap sm:px-6">
      <AccountLogo path={logoPath} wallet={!accountNumber && Boolean(qrPath)} />
      <div className="min-w-32 flex-1">
        <p className="text-sm font-semibold text-ink">{name}</p>
        <p className="text-xs break-words text-muted">
          {[accountNumber, accountName].filter(Boolean).join(' · ')}
        </p>
      </div>
      {accountNumber ? (
        <>
          <button
            type="button"
            onClick={() => void copy(accountNumber)}
            // The visible label is the same on every row; the name says which number it copies.
            aria-label={`${copyLabel} — ${name}`}
            className={actionLinkClass(
              cn(
                'inline-flex min-h-11 shrink-0 items-center gap-1 text-sm lg:min-h-0',
                failed && 'text-danger',
              ),
            )}
          >
            {copyLabel}
            <Icon name={failed ? 'error' : copied ? 'check' : 'content_copy'} size={16} />
          </button>
          <span aria-live="polite" className="sr-only">
            {copied || failed ? `${name} ${copyLabel}` : ''}
          </span>
        </>
      ) : qrPath ? (
        <button
          type="button"
          onClick={onShowQr}
          aria-label={`${t('chinaPayment.payTo.showQr')} — ${name}`}
          className={actionLinkClass(
            'inline-flex min-h-11 shrink-0 items-center gap-1 text-sm lg:min-h-0',
          )}
        >
          {t('chinaPayment.payTo.showQr')}
          <Icon name="qr_code_2" size={16} />
        </button>
      ) : null}
    </li>
  );
}

/**
 * The account's logo in a fixed box, so the rows line up: live they are 1000×226 and 120×40.
 * Decorative — the name sits beside it. Missing or broken (Maybank's, today) shows an icon.
 */
function AccountLogo({ path, wallet }: { path?: string; wallet: boolean }) {
  const [failed, setFailed] = useState(false);
  const url = path && !failed ? vendorAssetUrl(path, legacyAssetOrigin) : undefined;

  return (
    <span className="flex h-8 w-24 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-alt">
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-contain"
        />
      ) : (
        <Icon
          name={wallet ? 'qr_code_2' : 'account_balance'}
          size={20}
          className="text-muted-soft"
        />
      )}
    </span>
  );
}
